import { line } from '@bms2/shared';
import type { ReactNode } from 'react';
import { RadioGroup } from '../../components/controls';
import { DataTable, type DataTableColumn } from '../../components/composites/DataTable';
import styles from './MergeTable.module.css';

/** The first column's key, the field's label; a record's column is keyed by its id. */
const FIELD_COLUMN = '__field';

/** One record the merge table draws as a column: its name, its facts beneath, and any note under them. */
export interface MergeTableColumn {
  id: string;
  name: string;
  facts: ReactNode;
  note?: ReactNode;
}

/** One field the two records fill differently: its label and each record's value as a person reads it. */
export interface MergeTableRow {
  key: string;
  label: string;
  values: Readonly<Record<string, string>>;
}

export interface MergeTableProps {
  columns: readonly MergeTableColumn[];
  /** The record that stays, chosen in its column's head. */
  keepId: string;
  onKeep: (id: string) => void;
  /** The control's label in each column's head. */
  keepLabel: string;
  fieldLabel: string;
  rows: readonly MergeTableRow[];
  /** The column whose value a row takes. */
  pickOf: (key: string) => string;
  onPick: (key: string, columnId: string) => void;
  /** Whether a cell's value can be picked; a cell that cannot draws its value alone. Every cell can where it is absent (decision 7450: an empty cell of a set takes no pick). */
  pickable?: (key: string, columnId: string) => boolean;
  /** What the body says when no field is filled differently, or null while the values are read. */
  empty: string | null;
  disabled?: boolean;
}

/**
 * 🔒 The merge window's table (piece 10; decision 2501): a column per record
 * and a row per field the two fill differently. The one that stays is chosen
 * by a control in its column's head, and on each row the better value is
 * picked by a control in its own cell. A field only one record fills is taken
 * without asking and gets no row.
 *
 * The company and the person merge draw it, and a location merge, when one is
 * built, draws it too: it knows records only as columns of labelled values.
 * 🔨 One radio group per row spans its cells by sharing a name, so a row holds
 * exactly one pick however many columns it draws.
 */
export function MergeTable({ columns, keepId, onKeep, keepLabel, fieldLabel, rows, pickOf, onPick, pickable, empty, disabled = false }: MergeTableProps) {
  // T3272: the shared data table draws the head and the rows; the hooks and classes each record's head and each value cell carries come through its column and cell options.
  const tableColumns: DataTableColumn[] = [
    { key: FIELD_COLUMN, header: fieldLabel, headClass: styles.fieldHead },
    ...columns.map((column) => ({
      key: column.id,
      header: (
        <>
          <span className={styles.name}>{column.name}</span>
          {column.facts}
          <RadioGroup
            name="merge-keep"
            legend={line(keepLabel, '·', column.name)}
            legendHidden
            options={[{ value: column.id, label: keepLabel }]}
            value={keepId}
            onChange={onKeep}
            disabled={disabled}
          />
          {column.note}
        </>
      ),
      headClass: column.id === keepId ? styles.keptHead : styles.head,
      headData: { 'data-merge-side': column.id, 'data-merge-keep': column.id === keepId ? 'true' : 'false' } as const,
    })),
  ];
  return (
    <div className={styles.frame} data-merge-table={columns.length}>
      <DataTable
        columns={tableColumns}
        rows={rows}
        rowKey={(row) => row.key}
        rowHeader
        rowData={(row) => ({ 'data-merge-field': row.key, 'data-merge-pick': pickOf(row.key) })}
        cellClass={(row, columnId) => (columnId === FIELD_COLUMN ? styles.fieldHead : columnId === pickOf(row.key) ? styles.picked : styles.cell)}
        cellData={(_row, columnId) => (columnId === FIELD_COLUMN ? undefined : { 'data-merge-cell': columnId })}
        empty={empty === null ? null : <span className={styles.empty}>{empty}</span>}
        cell={(row, columnId) => {
          if (columnId === FIELD_COLUMN) return row.label;
          const column = columns.find((one) => one.id === columnId)!;
          return pickable && !pickable(row.key, columnId) ? (
            <span className={styles.unpickable} data-merge-unpickable="true">{row.values[columnId] ?? ''}</span>
          ) : (
            <RadioGroup
              name={`merge-field-${row.key}`}
              legend={line(row.label, '·', column.name)}
              legendHidden
              options={[{ value: columnId, label: row.values[columnId] ?? '' }]}
              value={pickOf(row.key)}
              onChange={(id) => onPick(row.key, id)}
              disabled={disabled}
            />
          );
        }}
      />
    </div>
  );
}
